// @flow
import React from 'react';
import { debounce } from 'lodash';

import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import Glyph from 'components/Glyph';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import MediaObject from 'components/MediaObject';
import ScrollContainer from 'components/ScrollContainer';
import StudentIdListTableRow from './StudentIdListTableRow';
import Table from 'components/Table';
import TextWithGlyph from 'components/TextWithGlyph';
import VisibilityWatch from 'components/VisibilityWatch';

import './styles/StudentIdListTable.scss';

type Props = {
  studentIdList: *,
  loading: boolean,
  orgUuid: string,
  onLoadMoreStudents: (cursor: string) => void,
  onParentAdd: () => void,
  eventKeys: {
    unidentifiedStudentsHelpCTA?: string,
    unidentifiedStudentsCollapse?: string,
    unidentifiedStudentsExpand?: string,
  },
};

type State = {
  showUnmatchedStudents: boolean,
  scrolledToBottom: boolean,
};

export default class StudentIdListTable extends React.Component<Props, State> {
  state: State = {
    showUnmatchedStudents: true,
    scrolledToBottom: false,
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { studentIdList } = this.props;

    if (!studentIdList || !studentIdList.matched.pageInfo.hasNextPage) {
      return;
    }

    const lastCursor = studentIdList.matched
      ? studentIdList.matched.edges.slice(-1)[0].cursor
      : null;
    if (!prevState.scrolledToBottom && this.state.scrolledToBottom && lastCursor) {
      this.props.onLoadMoreStudents(lastCursor);
    }
  }

  setScrolledToBottom = debounce(
    (scrolledToBottom: boolean) => {
      this.setState({ scrolledToBottom });
    },
    200,
    { leading: true, trailing: true }
  );

  toggleUnmatchedStudentVisibility = () => {
    this.setState({ showUnmatchedStudents: !this.state.showUnmatchedStudents });
  };

  renderVisibilityWatcher() {
    return (
      <VisibilityWatch
        whileVisible={() => this.setScrolledToBottom(true)}
        whileInvisible={() => this.setScrolledToBottom(false)}
        interval={500}>
        <div />
      </VisibilityWatch>
    );
  }

  renderUnmatchedStudents = (unmatchedStudents: *) => {
    const { eventKeys } = this.props;
    const { showUnmatchedStudents } = this.state;

    return (
      <tbody className="StudentIdListTable-section--unmatched">
        <tr>
          <td colSpan="3">
            <MediaObject figure="right" center>
              <TextWithGlyph glyph="caution" glyphSize="medium" glyphColor="pencil">
                {unmatchedStudents.length === 1
                  ? `There is ${unmatchedStudents.length} unidentified student. `
                  : `There are ${unmatchedStudents.length} unidentified students. `}
                <Link
                  href={LinkConstants.UNMATCHED_STUDENTS}
                  weight="semibold"
                  target="_blank"
                  eventKey={eventKeys.unidentifiedStudentsHelpCTA}>
                  Refer to this help article.
                </Link>
              </TextWithGlyph>
              <Button
                level="icon"
                aria-label={
                  showUnmatchedStudents ? 'Hide unmatched students' : 'Show unmatched students'
                }
                eventKey={
                  showUnmatchedStudents
                    ? eventKeys.unidentifiedStudentsCollapse
                    : eventKeys.unidentifiedStudentsExpand
                }
                onClick={this.toggleUnmatchedStudentVisibility}>
                <Glyph
                  name={showUnmatchedStudents ? 'chevron-up' : 'chevron-down'}
                  size="medium"
                  color="graphite"
                />
              </Button>
            </MediaObject>
          </td>
        </tr>
        {showUnmatchedStudents &&
          unmatchedStudents.map(unmatchedStudent => (
            <StudentIdListTableRow
              key={unmatchedStudent.studentId}
              student={unmatchedStudent}
              orgUuid={this.props.orgUuid}
            />
          ))}
      </tbody>
    );
  };

  renderMatchedStudents = (
    matchedStudents: *,
    includeParents: boolean,
    includeStudents: boolean
  ) => {
    return (
      <tbody className="StudentIdListTable-section--matched">
        {matchedStudents.map(matchedStudent => (
          <StudentIdListTableRow
            key={matchedStudent.studentId}
            student={matchedStudent}
            orgUuid={this.props.orgUuid}
            disableParent={!includeParents}
            disableStudent={!includeStudents}
            onParentAdd={this.props.onParentAdd}
          />
        ))}
      </tbody>
    );
  };

  render() {
    const {
      unmatched,
      matched,
      includeStudents,
      includeParents,
      unmatchedStudentsCount,
    } = this.props.studentIdList;

    const matchedStudents = matched.edges.map(edge => edge.node);
    const unmatchedStudents = unmatched.edges.map(edge => edge.node);

    return (
      <ScrollContainer>
        <div className="StudentIdListTable">
          <Table
            head={
              <tr>
                <th style={{ width: '35%' }}>Student Name</th>
                <th style={{ width: '27%' }}>Student Number</th>
                <th>Parents and Guardians</th>
              </tr>
            }
            equalWidthColumns>
            <React.Fragment>
              {!!unmatchedStudentsCount && this.renderUnmatchedStudents(unmatchedStudents)}
              {this.renderMatchedStudents(matchedStudents, includeParents, includeStudents)}
              {this.props.loading && (
                <tbody>
                  <tr>
                    <td colSpan={3}>
                      <CenteredLoadingIndicator />
                    </td>
                  </tr>
                </tbody>
              )}
            </React.Fragment>
          </Table>
          {this.renderVisibilityWatcher()}
        </div>
      </ScrollContainer>
    );
  }
}
